import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';

import {
  auditorReportAdd,
  auditorReportDetailsGet
} from '../../../store/slices/tenantSlices/auditorSlice';

import { fileDownload } from '../../../utils/common';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import FormTextarea from '../../../components/form/FormTextarea';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import JsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import config from '../../../config';
import { useLoginUserType } from '../../../store/slices/commonSlice';

const AddEditAuditReport = () => {
  const { call_history_id, archiveId } = useParams();
  const pdfRef = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginUserType = useLoginUserType();

  const [auditorDetails, setAuditorDetails] = useState(null);
  const [audioDetails, setAudioDetails] = useState(null);
  const [auditDetails, setAuditDetails] = useState([]);
  const [totalAvgScore, setTotalAvgScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [callTranscript, setCallTranscript] = useState('');
  const [behavior, setBehavior] = useState('');
  const [interestPercentage, setInterestPercentage] = useState(0);

  const fetchAuditorReportDetails = async () => {
    try {
      const response = await dispatch(
        auditorReportDetailsGet({ call_history_id: call_history_id, archiveId: archiveId })
      ).unwrap();
      const processedAuditDetails = (response?.auditDetails || []).map((category) => ({
        ...category,
        subcategories: category.subcategories.map((subcategory) => ({
          ...subcategory,
          applicable: subcategory?.applicable ? subcategory?.applicable : 'na',
          feedback: subcategory?.feedback ? subcategory?.feedback : ''
        }))
      }));
      setAuditorDetails(response?.callData);
      setAuditDetails(processedAuditDetails);
      setCallTranscript(response?.call_transcript || '');
      setInterestPercentage(response?.interest_percentage || 0);
      setBehavior(response?.behavior || '');
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (call_history_id) {
      fetchAuditorReportDetails();
    }
  }, [call_history_id]);

  const handleApplicableChange = (catIndex, subIndex, value) => {
    setAuditDetails((prevDetails) => {
      const updated = [...prevDetails];
      updated[catIndex] = {
        ...updated[catIndex],
        subcategories: [...updated[catIndex].subcategories]
      };
      updated[catIndex].subcategories[subIndex] = {
        ...updated[catIndex].subcategories[subIndex],
        applicable: value
      };
      return updated;
    });
  };

  const handleCommentChange = (catIndex, subIndex, value) => {
    setAuditDetails((prevDetails) => {
      const updated = [...prevDetails];
      updated[catIndex] = {
        ...updated[catIndex],
        subcategories: [...updated[catIndex].subcategories]
      };
      updated[catIndex].subcategories[subIndex] = {
        ...updated[catIndex].subcategories[subIndex],
        comment: value
      };
      return updated;
    });
  };

  const isAutoAudit = process.env.REACT_APP_AUTO_AUDIT_RESULT === 'true';

  const earnedPoints = (subcategories = []) =>
    subcategories.reduce((total, { applicable, score_weight = 0 }) => {
      if (isAutoAudit) {
        return applicable === 'yes' ? total + score_weight : total;
      }
      const value = Number(applicable);
      return !isNaN(value) ? total + value : total;
    }, 0);

  const applicablePoints = (subcategories = []) =>
    subcategories.reduce((total, { applicable, score_weight = 0 }) => {
      if (isAutoAudit) {
        return applicable === 'yes' || applicable === 'no' ? total + score_weight : total;
      }
      return !isNaN(Number(applicable)) ? total + score_weight : total;
    }, 0);

  const scoreAvg = (ePoint, aPoint) => {
    if (aPoint === 0) return 0;
    return parseFloat(((ePoint / aPoint) * 100).toFixed(2));
  };

  const processedDetails = useMemo(() => {
    return auditDetails.map((item) => {
      const subcategories = item.subcategories || [];
      const totalWeightage = subcategories.reduce((sum, sub) => sum + sub.score_weight, 0);
      const ePoints = earnedPoints(subcategories);
      const aPoints = applicablePoints(subcategories);
      const score = scoreAvg(ePoints, aPoints);

      return {
        ...item,
        totalWeightage,
        ePoints,
        aPoints,
        score: parseFloat(score.toFixed(2))
      };
    });
  }, [auditDetails]);

  useEffect(() => {
    if (!processedDetails.length) return;
    const validItems = processedDetails.filter((item) => item.aPoints > 0);
    const totalScore = validItems.reduce((sum, item) => sum + item.score, 0);
    const avg = validItems.length > 0 ? parseFloat((totalScore / validItems.length).toFixed(2)) : 0;

    setTotalAvgScore(avg);
  }, [processedDetails]);

  const transformAuditDetails = (auditDetails = []) => {
    return auditDetails.flatMap((category) =>
      category.subcategories.map((sub) => ({
        auditor_category_id: category.category_id,
        auditor_sub_category_id: sub.subcategory_id,
        comment: sub.comment,
        applicable: sub.applicable
      }))
    );
  };

  const onSubmitReport = async () => {
    const details = transformAuditDetails(auditDetails);
    try {
      const payload = {
        call_history_id: call_history_id,
        user_id: auditorDetails?.user_id,
        percentage: totalAvgScore,
        comment: feedback,
        process_id: auditorDetails?.process_id,
        details: [...(details || [])]
      };
      const response = await dispatch(auditorReportAdd(payload)).unwrap();
      if (response && response?.success) {
        toast.success('Audit saved successfully');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const exportPDF = () => {
    const reportElement = document.getElementById('audit-report');

    html2canvas(reportElement).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new JsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('audit-report.pdf');
    });
  };

  return (
    <React.Fragment>
      <AdminHeader
        title="Audit Report"
        isAdd
        isBack
        addBtnName="Save Report"
        isAddDisabled={loginUserType !== 'auditor'}
        onAddClick={() => onSubmitReport()}
        backBtnName="Back"
        onBackClick={() => navigate(-1)}
        isManualExport
        isExport
        onExportClick={() => exportPDF()}
      />
      <div id="audit-report" ref={pdfRef}>
        <Card className="mb-2 p-2">
          <CardTitle className="px-2">Auditor Details</CardTitle>
          <CardBody className="py-0 pb-2">
            <Row>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Name
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary"
                style={{ fontSize: '12px' }}>
                {auditorDetails?.Username}
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Number
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary"
                style={{ fontSize: '12px' }}>
                {auditorDetails?.crm_number}
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Process Name
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary"
                style={{ fontSize: '12px' }}>
                {auditorDetails?.name}
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Date
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary"
                style={{ fontSize: '12px' }}>
                {auditorDetails?.start_stamp}
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Duration & Disposition
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary d-flex align-items-center gap-2"
                style={{ fontSize: '12px' }}>
                <span>{auditorDetails?.duration}</span>
                <span>|</span>
                <span>{auditorDetails?.disposition}</span>
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-primary text-white"
                style={{ fontSize: '12px' }}>
                Recording
              </Col>
              <Col
                lg="2"
                md="2"
                className="py-1 bg-light text-primary"
                style={{ fontSize: '12px' }}>
                <div className="d-flex gap-2">
                  <Icon.PlayCircle
                    size={16}
                    onClick={() =>
                      setAudioDetails({
                        src: `${config.BASE_URL}cdrs_report/download?filename=${auditorDetails.recording_file}`
                      })
                    }
                    className="cursor-pointer text-info"
                  />
                  <Icon.Download
                    size={16}
                    onClick={() => {
                      fileDownload(
                        `${config.BASE_URL}cdrs_report/download?filename=${auditorDetails.recording_file}`
                      );
                    }}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Card className="mb-2 p-2">
          <CardTitle className="px-2">Auditor Score Summary</CardTitle>
          <CardBody className="" style={{ padding: '0 4px 8px 0' }}>
            <div
              className="daily-report-table"
              // style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}
            >
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-center" style={{ width: '180px' }}>
                      Weightage
                    </th>
                    <th className="text-center" style={{ width: '180px' }}>
                      Applicable Points
                    </th>
                    <th className="text-center" style={{ width: '180px' }}>
                      Earned Points
                    </th>
                    <th className="text-center" style={{ width: '180px' }}>
                      Score
                    </th>
                    <th className="text-center" style={{ width: '220px' }}>
                      Quality
                    </th>
                    <th className="text-center" style={{ width: '180px' }}>
                      Interest
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {processedDetails.map((item, index, array) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-light' : ''}>
                      <td>{item.category_name}</td>
                      <td className="text-center">{item.totalWeightage}</td>
                      <td className="text-center">{item.aPoints}</td>
                      <td className="text-center">{item.ePoints}</td>
                      <td className="text-center">{item.score}%</td>
                      {index === 0 && (
                        <td rowSpan={array.length} className="position-relative">
                          <div
                            className={`text-center fw-bolder fs-3 ${totalAvgScore >= 90 ? 'text-success' : totalAvgScore < 85 ? 'text-danger' : 'text-warning'}`}
                            style={{
                              marginBottom: '15px'
                            }}>
                            {totalAvgScore}%
                          </div>
                          <div
                            className="d-flex align-items-center gap-2 text-primary position-absolute"
                            style={{ fontSize: '10px', bottom: '8px' }}>
                            <span className="d-flex align-items-center justify-content-center gap-1">
                              <div
                                className="bg-danger rounded"
                                style={{ width: '10px', height: '10px' }}
                              />
                              {'<85%'}
                            </span>
                            <span className="d-flex align-items-center justify-content-center gap-1">
                              <div
                                className="bg-warning rounded"
                                style={{ width: '10px', height: '10px' }}
                              />
                              {'>=85% & <90%'}
                            </span>
                            <span className="d-flex align-items-center justify-content-center gap-1">
                              <div
                                className="bg-success rounded"
                                style={{ width: '10px', height: '10px' }}
                              />
                              {'>=90%'}
                            </span>
                          </div>
                        </td>
                      )}
                      {index === 0 && (
                        <td rowSpan={array.length}>
                          <div className={`text-center fw-bolder fs-3`}>{interestPercentage}%</div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
        {callTranscript ? (
          <Card className="mb-2 p-2">
            <CardTitle className="px-2">Call Transcript</CardTitle>
            <CardBody className="py-0 pb-2 px-0">
              <p className="px-2 m-0">{callTranscript}</p>
            </CardBody>
          </Card>
        ) : null}
        {behavior ? (
          <Card className="mb-2 p-2">
            <CardTitle className="px-2">Behavior</CardTitle>
            <CardBody className="py-0 pb-2 px-0">
              <p className="px-2 m-0">{behavior}</p>
            </CardBody>
          </Card>
        ) : null}
        <Card className="mb-2 p-2">
          <CardTitle className="px-2">Audit Details</CardTitle>
          <CardBody className="" style={{ padding: '0 4px 8px 0' }}>
            <div className="audit-report-table">
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th style={{ width: '180px' }}>Weightage</th>
                    <th style={{ width: '360px' }}>Parameter</th>
                    <th style={{ width: '180px' }}>Audit Result</th>
                    <th style={{ width: '220px' }}>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {auditDetails?.map((item, catIndex) => {
                    const subcategories = item.subcategories;
                    return subcategories.map((sub, subIndex) => {
                      const isAutoAudit = process.env.REACT_APP_AUTO_AUDIT_RESULT === 'true';
                      const selectOptions = isAutoAudit
                        ? [
                            { label: 'N/A', value: 'na' },
                            { label: 'Yes', value: 'yes' },
                            { label: 'No', value: 'no' }
                          ]
                        : [
                            { label: 'N/A', value: 'na' },
                            ...Array.from({ length: sub.score_weight + 1 }, (_, i) => ({
                              label: String(i),
                              value: String(i)
                            }))
                          ];

                      return (
                        <tr
                          key={`cat-${catIndex}-sub-${subIndex}`}
                          className={catIndex % 2 === 0 ? 'bg-light text-primary' : 'text-primary'}>
                          {subIndex === 0 && (
                            <td rowSpan={subcategories.length}>
                              <span className="text-primary">{item.category_name}</span>
                            </td>
                          )}
                          <td>{sub.score_weight}</td>
                          <td className="text-wrap w-50">{sub.criteria_description}</td>
                          <td className="p-0">
                            <FormSelect
                              disabled={loginUserType !== 'auditor'}
                              className="table-select report-table"
                              isShowLabel={false}
                              placeholder="Select Field"
                              value={sub.applicable}
                              options={selectOptions}
                              onChange={(e) =>
                                handleApplicableChange(catIndex, subIndex, e.target.value)
                              }
                            />
                          </td>
                          <td className="p-0">
                            <FormInput
                              disabled={loginUserType !== 'auditor'}
                              isShowLabel={false}
                              className="callex-mb-0 report-table"
                              value={sub.comment}
                              name="comment"
                              onChange={(e) =>
                                handleCommentChange(catIndex, subIndex, e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
        <Card className="mb-2 p-2">
          <CardTitle className="px-2">Feedback/Remarks</CardTitle>
          <CardBody className="py-0 pb-2 px-0">
            <FormTextarea
              name="feedback"
              isShowLabel={false}
              className="callex-mb-0"
              placeholder="enter your feedback here.."
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
            />
          </CardBody>
        </Card>
      </div>
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </React.Fragment>
  );
};

export default AddEditAuditReport;
